/*
 * Gilded 003 free-gift-with-purchase (GWP) auto-add.
 *
 * Loaded globally from layout/theme.liquid (not just the machine PDP) so the
 * gift line is reconciled on every page — dropping below the eligibility rule
 * anywhere drops the $0 gift, closing the orphaned-free-item loophole. Keeps the
 * cart in sync with a single rule: while the cart qualifies there is always
 * exactly one $0 gift bag (Gilded 003 Lonya Grande); when it does not qualify
 * there is none. It reconciles the cart after every cart mutation, so it covers
 * the standard Add-to-cart form, the Memorial / Buy-Now bundle CTA, upsells and
 * minicart quantity/remove changes without patching each handler individually.
 *
 * Eligibility is CLIENT-CONFIGURABLE from Theme settings → Gift with Purchase
 * (GWP) and is emitted on window.__machineGift by layout/theme.liquid:
 *   {
 *     giftVariantId: <number>,      // the $0 gift variant
 *     machineProductId: <number>,   // machine product, used by "machine" mode
 *     enabled: <boolean>,           // settings.gwp_enabled (default true)
 *     mode: "machine"|"threshold"|"any",  // settings.gwp_mode (default machine)
 *     thresholdCents: <number>,     // settings.gwp_threshold * 100
 *     giveawayEnabled: <boolean>,   // settings.gwp_giveaway_enabled (default false)
 *     giveawayPrefix: <string>      // settings.gwp_giveaway_prefix (default "")
 *   }
 *
 * X / Twitter machine giveaway suppression (giveawayEnabled, default OFF):
 * winners redeem a single-use discount code (shared prefix, e.g. "GIVEAWAY-")
 * delivered as a /discount/CODE link, and must NOT receive the auto-added $0
 * Gilded gift. When suppression is on, a cart is treated as a giveaway — and the
 * gift is withheld / removed — if EITHER signal is present:
 *   (a) the cart carries a discount code (cart.discount_codes /
 *       *discount_applications / per-line discounts) whose code or title starts
 *       with the configured prefix; this surfaces in /cart.js before checkout, and
 *   (b) a localStorage "cumulusGiveaway" flag set by snippet c-giveaway-flag when
 *       the shopper arrives via .../?giveaway=1 (the giveaway link's redirect
 *       target). The flag is a self-expiring belt-and-suspenders fallback for the
 *       case where the code is not yet reflected in the cart.
 * With giveawayEnabled false (the default) neither signal is consulted, so
 * shipping this changes nothing until the client flips it on.
 *
 * Defaults (enabled + machine mode, giveaway off) reproduce the original
 * machine-gated behavior, so shipping this changes nothing until the client
 * flips the mode.
 *
 * The gift is added as a separate $0 line — always exactly one per qualifying
 * order (never one-per-$100) — so it never touches the machine's price, selling
 * plan or any stacked discount code.
 */
(function () {
  "use strict";

  var root =
    typeof window !== "undefined"
      ? window
      : typeof globalThis !== "undefined"
      ? globalThis
      : this;

  /*
   * Collect every discount code / application label attached to the cart across
   * the shapes /cart.js can use, so prefix-matching is robust to Shopify's
   * payload variations:
   *   - cart.discount_codes[].code            (code applied via /discount/CODE)
   *   - cart.*discount_applications[].code|title
   *   - items[].discounts[].title             (line-level allocations)
   */
  function cartDiscountLabels(cart) {
    var out = [];
    if (!cart) return out;
    function pushFrom(list) {
      if (!list || !list.length) return;
      for (var i = 0; i < list.length; i++) {
        var d = list[i] || {};
        if (d.code) out.push(String(d.code));
        if (d.title) out.push(String(d.title));
      }
    }
    pushFrom(cart.discount_codes);
    pushFrom(cart.discount_applications);
    pushFrom(cart.cart_level_discount_applications);
    var items = cart.items || [];
    for (var j = 0; j < items.length; j++) pushFrom((items[j] || {}).discounts);
    return out;
  }

  /*
   * Whether the giveaway rule should withhold the gift for this cart. Master
   * switch off (default) -> never. Otherwise either signal suppresses:
   *   (a) an applied discount code/label starts with the configured prefix, or
   *   (b) opts.giveawayFlag (the localStorage flag from the ?giveaway=1 landing).
   */
  function giveawaySuppressed(cart, opts) {
    if (!opts || !opts.giveawayEnabled) return false;
    if (opts.giveawayFlag) return true;
    var prefix = String(opts.giveawayPrefix || "").toUpperCase();
    if (!prefix) return false;
    var labels = cartDiscountLabels(cart);
    for (var i = 0; i < labels.length; i++) {
      if (String(labels[i]).toUpperCase().indexOf(prefix) === 0) return true;
    }
    return false;
  }

  /*
   * Pure eligibility test. Given the /cart.js payload and the resolved config,
   * decide whether the cart should carry the one $0 gift line. The $0 gift line
   * itself is always excluded from the calculation so it can never keep itself
   * alive.
   *
   * opts: { enabled, mode, thresholdCents, machineId, giftId,
   *         giveawayEnabled, giveawayPrefix, giveawayFlag }
   */
  function qualifies(cart, opts) {
    if (!opts || !opts.enabled) return false;

    // Giveaway winners redeeming a code (or arriving via the giveaway link) must
    // never get the free gift, whatever the GWP mode. Checked before mode logic
    // so the global reconciler strips any lingering $0 gift the moment the code
    // lands on the cart.
    if (giveawaySuppressed(cart, opts)) return false;

    var items = (cart && cart.items) || [];
    var giftId = String(opts.giftId || "");
    var machineId = String(opts.machineId || "");
    var mode = opts.mode || "machine";

    // Everything except the $0 gift line.
    var nonGift = [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) !== giftId) nonGift.push(items[i]);
    }

    if (mode === "any") {
      // At least one real (non-gift) line item present.
      return nonGift.length > 0;
    }

    if (mode === "threshold") {
      // Shopify's original total is the gross cart value before discounts. Exclude
      // gift original amounts so gift merchandise never helps meet the threshold.
      var subtotal =
        cart && typeof cart.original_total_price === "number"
          ? cart.original_total_price
          : 0;
      var giftLineTotal = 0;
      for (var k = 0; k < items.length; k++) {
        if (String(items[k].variant_id) === giftId) {
          giftLineTotal +=
            typeof items[k].original_line_price === "number"
              ? items[k].original_line_price
              : 0;
        }
      }
      return subtotal - giftLineTotal >= Number(opts.thresholdCents || 0);
    }

    // Default / "machine": a Cumulus machine is in the cart.
    for (var j = 0; j < nonGift.length; j++) {
      if (String(nonGift[j].product_id) === machineId) return true;
    }
    return false;
  }

  // Expose the pure helpers for unit tests (harmless in the browser).
  if (root) {
    root.__machineGiftQualifies = qualifies;
    root.__machineGiftGiveawaySuppressed = giveawaySuppressed;
  }

  /*
   * Read the giveaway suppression flag written by snippet c-giveaway-flag when
   * the shopper lands on a ?giveaway=1 URL. Stored as JSON { exp: <ms epoch> }
   * with a short TTL so the fallback can never leak into a later normal order;
   * an expired flag is cleared on read. Returns true only while unexpired.
   */
  function readGiveawayFlag(win) {
    try {
      var raw = win.localStorage.getItem("cumulusGiveaway");
      if (!raw) return false;
      var data = JSON.parse(raw);
      if (data && typeof data.exp === "number" && data.exp > new Date().getTime()) {
        return true;
      }
      win.localStorage.removeItem("cumulusGiveaway");
      return false;
    } catch (e) {
      return false;
    }
  }

  function boot(win) {
    var cfg = win.__machineGift || {};
    var GIFT_ID = String(cfg.giftVariantId || "");
    var MACHINE_ID = String(cfg.machineProductId || "");

    // Bail if we weren't handed the gift/machine ids we need to find lines.
    if (!GIFT_ID || !MACHINE_ID) return;

    var CFG = {
      // Checkbox defaults true; treat only an explicit false as disabled so a
      // missing setting (before it reaches prod) still behaves as enabled.
      enabled: cfg.enabled !== false,
      mode: cfg.mode || "machine",
      thresholdCents: Number(cfg.thresholdCents || 0),
      machineId: MACHINE_ID,
      giftId: GIFT_ID,
      // Giveaway suppression: opt-in master switch (default off) + code prefix.
      // giveawayFlag is refreshed from localStorage on every reconcile pass.
      giveawayEnabled: cfg.giveawayEnabled === true,
      giveawayPrefix: String(cfg.giveawayPrefix || ""),
      giveawayFlag: false,
    };

    // Use the native fetch for our own reads/writes so they never re-trigger the
    // reconcile hook installed below (which would recurse).
    var origFetch = win.fetch.bind(win);

    var busy = false; // a reconcile pass (including its own writes) is running
    var again = false; // a mutation arrived mid-pass; run one more pass after

    function getJSON(url, opts) {
      return origFetch(url, opts).then(function (r) {
        return r.json();
      });
    }

    function write(url, body) {
      return getJSON(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      }).then(function () {
        return true;
      });
    }

    // Re-render the minicart / cart totals using the theme's own helpers.
    function refreshUI() {
      try {
        if (typeof cCart === "undefined") return;
        if (typeof cCart.updateCartContent === "function") cCart.updateCartContent();
        if (typeof cCart.updateCartStats === "function") cCart.updateCartStats();
      } catch (e) {}
    }

    function reconcile() {
      if (busy) {
        again = true;
        return;
      }
      busy = true;

      getJSON("/cart.js", { headers: { Accept: "application/json" } })
        .then(function (cart) {
          var items = (cart && cart.items) || [];
          var gift = null;

          for (var i = 0; i < items.length; i++) {
            if (String(items[i].variant_id) === GIFT_ID) gift = items[i];
          }

          // Refresh the giveaway localStorage flag each pass (only matters when
          // suppression is enabled) so a landing that happens after boot is seen.
          CFG.giveawayFlag = CFG.giveawayEnabled ? readGiveawayFlag(win) : false;

          var wanted = qualifies(cart, CFG);

          // Qualifies, gift missing -> add exactly one.
          if (wanted && !gift) {
            return write("/cart/add.js", {
              items: [{ id: Number(GIFT_ID), quantity: 1 }],
            });
          }
          // Qualifies, gift qty drifted -> pin to one (one per order, never one-per-$100).
          if (wanted && gift && gift.quantity !== 1) {
            return write("/cart/change.js", { id: gift.key, quantity: 1 });
          }
          // No longer qualifies but a gift lingers -> drop it.
          if (!wanted && gift) {
            return write("/cart/change.js", { id: gift.key, quantity: 0 });
          }
          // Already consistent.
          return null;
        })
        .then(function (changed) {
          busy = false;
          if (changed) refreshUI();
          if (again) {
            again = false;
            reconcile();
          }
        })
        .catch(function (e) {
          busy = false;
          console.error("[machine-gift] reconcile failed:", e);
          if (again) {
            again = false;
            reconcile();
          }
        });
    }

    // React to any cart mutation triggered elsewhere on the page. Our own writes
    // go through origFetch above, so they are never seen here.
    var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    win.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var p = origFetch(input, init);
      if (CART_MUTATION.test(url)) {
        p.then(function () {
          reconcile();
        }).catch(function () {});
      }
      return p;
    };

    // Initial pass: covers returning carts, orphaned gifts and qty drift.
    if (win.document && win.document.readyState === "loading") {
      win.document.addEventListener("DOMContentLoaded", reconcile);
    } else {
      reconcile();
    }
  }

  // Only wire up cart reconciliation in a real browser document. Under Node
  // (unit tests) there is no document, so the pure qualifies() export is all
  // that runs.
  if (root && typeof root.document !== "undefined" && root.document) {
    boot(root);
  }
})();
